import { useEffect, useState } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Upload,
  Image,
  Tag,
  message,
  Popconfirm,
  Row,
  Col,
  Card,
  Statistic,
  Tabs,
  Tree,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ExportOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  StopOutlined,
  UploadOutlined,
  DownloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons'
import { productAPI, categoryAPI, uploadAPI, API_BASE_URL, ASSETS_BASE_URL } from '../../api'
import { getToken } from '../../utils/auth'
import { confirmDelete, confirmStatusChange } from '../../hooks/useConfirm'

const { Option } = Select
const { TextArea } = Input

interface Category {
  id: number
  name: string
  level: number
  parentId: number | null
  children?: Category[]
}

interface Sku {
  id: number
  spec: string
  stock: number
  stockWarning: number
}

interface Product {
  id: number
  name: string
  description: string
  image: string
  images?: string[]
  categoryId: number
  categoryName: string
  isActive: boolean
  skus: Sku[]
  createdAt: string
  storageTemp?: string
  origin?: string
  shelfLife?: string
  introText?: string
  introImages?: string[]
}

// Helper: ensure image URL has full API domain
const getImageUrl = (path: string) => {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) return path
  return `${ASSETS_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`
}

const Products = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [filterCategory, setFilterCategory] = useState<number | 'all'>('all')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('list')
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  const [categoryModalVisible, setCategoryModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [categoryForm] = Form.useForm()
  const [categoryViewMode, setCategoryViewMode] = useState<'table' | 'tree'>('table')
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>(undefined)

  const [importing, setImporting] = useState(false)

  const [previewImage, setPreviewImage] = useState('')
  const [previewImages, setPreviewImages] = useState<string[]>([])
  const [previewIntroImages, setPreviewIntroImages] = useState<string[]>([])

  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    lowStockProducts: 0,
    outOfStockProducts: 0,
  })

  useEffect(() => {
    fetchCategories()
  }, [])
  useEffect(() => {
    fetchProducts()
  }, [filterCategory, filterStatus, pagination.current, pagination.pageSize])

  const fetchCategories = async () => {
    try {
      const res = await categoryAPI.getTree()
      if (res.code === 200) setCategories(res.data || [])
    } catch (error) {
      message.error('获取分类失败')
    }
  }

  const fetchProducts = async (page?: number) => {
    setLoading(true)
    try {
      const params: any = {
        page: page ?? pagination.current,
        pageSize: pagination.pageSize,
        _t: Date.now(),
      }
      if (searchKeyword) params.keyword = searchKeyword
      if (filterCategory !== 'all') params.categoryId = filterCategory
      if (filterStatus !== 'all') params.status = filterStatus
      const res = await productAPI.getList(params)
      if (res.code === 200) {
        setProducts(res.data.list || [])
        setPagination(prev => ({ ...prev, total: res.data.total || 0, current: page ?? prev.current }))
        if (res.data.stats) setStats(res.data.stats)
      }
    } catch (error) {
      message.error('获取商品失败')
    } finally {
      setLoading(false)
    }
  }

  const refreshProducts = () => {
    fetchProducts(1)
  }
  const handleSearch = () => {
    fetchProducts(1)
  }

  const handleImport = async (options: any) => {
    setImporting(true)
    const formData = new FormData()
    formData.append('file', options.file)
    try {
      const token = getToken()
      const res = await fetch(`${API_BASE_URL}/import/products`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      const data = await res.json()
      if (data.code === 200) {
        message.success(data.message)
        refreshProducts()
        fetchCategories()
      } else message.error(data.message)
    } catch (error) {
      message.error('导入失败')
    } finally {
      setImporting(false)
    }
  }

  const handleDownloadTemplate = () =>
    window.open(`${API_BASE_URL}/import/template`)

  const handleAdd = () => {
    setEditingProduct(null)
    setPreviewImage('')
    setPreviewImages([])
    setPreviewIntroImages([])
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (record: Product) => {
    setEditingProduct(record)
    setPreviewImage(getImageUrl(record.image || ''))
    const imgs = Array.isArray(record.images) 
          ? record.images.map((img: string) => getImageUrl(img))
          : typeof record.images === 'string' 
            ? (() => { try { const parsed = JSON.parse(record.images); return Array.isArray(parsed) ? parsed.map((img: string) => getImageUrl(img)) : [] } catch { return [] } })()
            : []
        setPreviewImages(imgs)
    const introImgs = Array.isArray(record.introImages)
          ? record.introImages.map((img: string) => getImageUrl(img))
          : typeof record.introImages === 'string'
            ? (() => { try { const parsed = JSON.parse(record.introImages); return Array.isArray(parsed) ? parsed.map((img: string) => getImageUrl(img)) : [] } catch { return [] } })()
            : []
        setPreviewIntroImages(introImgs)

    let safeSkus: any[] = []
    if (Array.isArray(record.skus)) {
      safeSkus = record.skus.map((sku: any) => ({
        spec: sku.spec || '',
        price: sku.price || 0,
        stock: sku.stock || 0,
        stockWarning: sku.stockWarning || 10,
        id: sku.id || undefined,
      }))
    }

    form.resetFields()
    setTimeout(() => {
      form.setFieldsValue({
        name: record.name || '',
        description: record.description || '',
        image: getImageUrl(record.image || ''),
        images: Array.isArray(record.images) ? record.images.map((img: string) => getImageUrl(img)) : [],
        categoryId: record.categoryId,
        isActive: record.isActive,
        skus: safeSkus,
        storageTemp: record.storageTemp || '',
        origin: record.origin || '',
        shelfLife: record.shelfLife || '',
        introText: record.introText || '',
        introImages: Array.isArray(record.introImages) ? record.introImages.map((img: string) => getImageUrl(img)) : [],
      })
    }, 100)
    setModalVisible(true)
  }

  const handleDelete = (id: number, name: string) => {
    confirmDelete(name, '商品管理', () => productAPI.delete(id).then(() => {
      message.success('删除成功')
      refreshProducts()
    }))
  }

  const handleToggleStatus = (id: number, isActive: boolean, name: string) => {
    confirmStatusChange(name, isActive, '商品管理', () =>
      productAPI.toggleStatus(id, isActive).then(() => {
        message.success(isActive ? '已上架' : '已下架')
        refreshProducts()
      })
    )
  }

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的商品')
      return
    }
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个商品吗？此操作不可恢复。`,
      okText: '确认删除',
      okType: 'danger',
      onOk: async () => {
        try {
          await productAPI.batchDelete(selectedRowKeys as number[])
          setSelectedRowKeys([])
          message.success('批量删除成功')
          refreshProducts()
        } catch (error) {
          message.error('批量删除失败')
        }
      },
    })
  }

  const handleBatchToggle = (isActive: boolean) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择商品')
      return
    }
    Modal.confirm({
      title: isActive ? '确认批量上架' : '确认批量下架',
      content: `确定要${isActive ? '上架' : '下架'}选中的 ${selectedRowKeys.length} 个商品吗？`,
      okText: '确认',
      onOk: async () => {
        try {
          await productAPI.batchToggleStatus(selectedRowKeys as number[], isActive)
          setSelectedRowKeys([])
          message.success(isActive ? '批量上架成功' : '批量下架成功')
          refreshProducts()
        } catch (error) {
          message.error('操作失败')
        }
      },
    })
  }

  const handleMoveProduct = async (index: number, direction: 'up' | 'down') => {
    const list = [...products]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= list.length) return
    ;[list[index], list[targetIndex]] = [list[targetIndex], list[index]]
    try {
      const token = getToken()
      await fetch(`${API_BASE_URL}/products/batch-sort`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: list.map((p, i) => ({ id: p.id, sort: i })),
        }),
      })
      setProducts(list)
      message.success('排序已更新')
    } catch (error) {
      message.error('排序更新失败')
    }
  }

  const handleMoveCategory = async (index: number, direction: 'up' | 'down') => {
    const list = [...flattenedCategories]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= list.length) return
    ;[list[index], list[targetIndex]] = [list[targetIndex], list[index]]
    try {
      const token = getToken()
      await fetch(`${API_BASE_URL}/categories/batch-sort`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: list.map((c, i) => ({ id: c.id, sort: i })),
        }),
      })
      message.success('排序已更新')
      fetchCategories()
    } catch (error) {
      message.error('排序更新失败')
    }
  }

  const handleImageUpload = async (file: File) => {
    try {
      const res = await uploadAPI.uploadImage(file)
      if (res.code === 200) {
        const url = res.data.url || res.data.path || res.data
        const fullUrl = typeof url === 'string' && !url.startsWith('http') ? `${ASSETS_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}` : url
        setPreviewImage(fullUrl)
        form.setFieldsValue({ image: fullUrl })
        message.success('上传成功')
      }
    } catch (error: any) {
      message.error(error?.response?.status === 401 ? '登录已过期' : '上传失败')
    }
  }

  const handleImagesUpload = async (file: File) => {
    try {
      const res = await uploadAPI.uploadImage(file)
      if (res.code === 200) {
        const url = res.data.url || res.data.path || res.data
        const fullUrl = typeof url === 'string' && !url.startsWith('http') ? `${ASSETS_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}` : url
        const imgs = [...previewImages, fullUrl]
        setPreviewImages(imgs)
        form.setFieldsValue({ images: imgs })
        message.success('上传成功')
      }
    } catch (error: any) {
      message.error(error?.response?.status === 401 ? '登录已过期' : '上传失败')
    }
  }

  const handleIntroImagesUpload = async (file: File) => {
    try {
      const res = await uploadAPI.uploadImage(file)
      if (res.code === 200) {
        const url = res.data.url || res.data.path || res.data
        const fullUrl = typeof url === 'string' && !url.startsWith('http') ? `${ASSETS_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}` : url
        const imgs = [...previewIntroImages, fullUrl]
        setPreviewIntroImages(imgs)
        form.setFieldsValue({ introImages: imgs })
        message.success('上传成功')
      }
    } catch (error: any) {
      message.error(error?.response?.status === 401 ? '登录已过期' : '上传失败')
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const productData = {
        ...values,
        image: previewImage,
        images: previewImages,
        introImages: previewIntroImages,
      }
      if (editingProduct) {
        await productAPI.update(editingProduct.id, productData)
        message.success('更新成功')
      } else {
        await productAPI.create(productData)
        message.success('添加成功')
      }
      setModalVisible(false)
      setEditingProduct(null)
      refreshProducts()
    } catch (error: any) {
      if (error.errorFields) return
      message.error(error.message || '操作失败')
    }
  }

  const handleAddCategory = () => {
    setEditingCategory(null)
    categoryForm.resetFields()
    categoryForm.setFieldsValue({ level: 1, parentId: undefined })
    setCategoryModalVisible(true)
  }
  const handleEditCategory = (record: Category) => {
    setEditingCategory(record)
    categoryForm.setFieldsValue({ ...record, parentId: record.parentId || undefined })
    setCategoryModalVisible(true)
  }
  const handleDeleteCategory = (id: number, name: string) => {
    confirmDelete(name, '分类管理', () =>
      categoryAPI.delete(id).then(() => {
        message.success('删除成功')
        fetchCategories()
      })
    )
  }
  const handleCategorySubmit = async () => {
    try {
      const values = await categoryForm.validateFields()
      if (values.parentId) {
        const parentCategory = findCategoryById(categories, values.parentId)
        if (parentCategory && parentCategory.level >= values.level) {
          message.error('父级分类的层级必须小于当前分类')
          return
        }
      }
      if (editingCategory) {
        await categoryAPI.update(editingCategory.id, values)
        message.success('更新成功')
      } else {
        await categoryAPI.create(values)
        message.success('添加成功')
      }
      setCategoryModalVisible(false)
      fetchCategories()
    } catch (error) {
      message.error('操作失败')
    }
  }

  const findCategoryById = (list: Category[], id: number): Category | null => {
    for (const item of list) {
      if (item.id === id) return item
      if (item.children) {
        const found = findCategoryById(item.children, id)
        if (found) return found
      }
    }
    return null
  }

  const handleCategoryLevelChange = (level: number) => {
    categoryForm.setFieldValue('parentId', undefined)
  }

  const flattenCategories = (list: Category[]): Category[] => {
    const result: Category[] = []
    const traverse = (items: Category[]) => {
      items.forEach(item => {
        result.push({ ...item, children: undefined })
        if (item.children) traverse(item.children)
      })
    }
    traverse(list)
    return result
  }

  const buildCategoryTree = (parentId: number | null = null): any[] => {
    return categories
      .filter(c => c.parentId === parentId)
      .map(c => ({
        key: c.id,
        title: (
          <Space>
            <span>{c.name}</span>
            <Tag color={c.level === 1 ? 'blue' : c.level === 2 ? 'green' : 'orange'}>
              {c.level}级
            </Tag>
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditCategory(c)}
              />
              <Popconfirm
                title="确定删除吗？"
                onConfirm={() => handleDeleteCategory(c.id, c.name)}
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Space>
          </Space>
        ),
        children: c.children ? buildCategoryTreeFromChildren(c.children) : [],
      }))
  }

  const buildCategoryTreeFromChildren = (children: Category[]): any[] => {
    return children.map(c => ({
      key: c.id,
      title: (
        <Space>
          <span>{c.name}</span>
          <Tag color={c.level === 1 ? 'blue' : c.level === 2 ? 'green' : 'orange'}>
            {c.level}级
          </Tag>
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditCategory(c)}
            />
            <Popconfirm
              title="确定删除吗？"
              onConfirm={() => handleDeleteCategory(c.id, c.name)}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Space>
        </Space>
      ),
      children: c.children ? buildCategoryTreeFromChildren(c.children) : [],
    }))
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: '图片',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (val: string) =>
        val ? (
          <Image
            src={getImageUrl(val)}
            width={50}
            height={50}
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <span>-</span>
        ),
    },
    { title: '商品名称', dataIndex: 'name', key: 'name', width: 150 },
    { title: '分类', dataIndex: 'categoryName', key: 'categoryName', width: 100 },
    {
      title: '规格/库存',
      key: 'skus',
      width: 200,
      render: (_: any, record: Product) => (
        <div>
          {Array.isArray(record.skus)
            ? record.skus.map(sku => (
                <div
                  key={sku.id}
                  style={{ fontSize: 12, marginBottom: 4 }}
                >
                  <span>{sku.spec}: </span>
                  <span style={{ marginLeft: 8 }}>
                    {sku.stock === 0 ? (
                      <Tag color="red">售罄</Tag>
                    ) : sku.stock <= (sku.stockWarning || 10) ? (
                      <Tag color="orange">库存紧张({sku.stock})</Tag>
                    ) : (
                      <span style={{ color: '#52c41a' }}>库存{sku.stock}</span>
                    )}
                  </span>
                </div>
              ))
            : <span>-</span>}
        </div>
      ),
    },
    {
      title: '产地',
      dataIndex: 'origin',
      key: 'origin',
      width: 100,
      render: (val: string) => val || '-',
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (val: boolean) => (
        <Tag icon={val ? <CheckCircleOutlined /> : <StopOutlined />} color={val ? 'green' : 'gray'}>
          {val ? '上架' : '下架'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right' as const,
      render: (_: any, record: Product, index: number) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<ArrowUpOutlined />}
            onClick={() => handleMoveProduct(index, 'up')}
          />
          <Button
            type="link"
            size="small"
            icon={<ArrowDownOutlined />}
            onClick={() => handleMoveProduct(index, 'down')}
          />
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleStatus(record.id, !record.isActive, record.name)}
          >
            {record.isActive ? '下架' : '上架'}
          </Button>
          <Popconfirm
            title="确定删除吗？"
            onConfirm={() => handleDelete(record.id, record.name)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  }
  const flattenedCategories = flattenCategories(categories)

  return (
    <div className="products-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="商品总数" value={stats.totalProducts} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="上架商品"
              value={stats.activeProducts}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="库存预警"
              value={stats.lowStockProducts}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="售罄商品"
              value={stats.outOfStockProducts}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="商品列表" key="list">
            <Space wrap style={{ marginBottom: 16 }}>
              <Input
                placeholder="搜索商品名称"
                prefix={<SearchOutlined />}
                style={{ width: 200 }}
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                onPressEnter={handleSearch}
              />
              <Select
                style={{ width: 150 }}
                value={filterCategory}
                onChange={setFilterCategory}
                allowClear
                placeholder="全部分类"
              >
                <Option value="all">全部分类</Option>
                {flattenedCategories.map(c => (
                  <Option key={c.id} value={c.id}>
                    {'-'.repeat(c.level - 1)} {c.name}
                  </Option>
                ))}
              </Select>
              <Select
                style={{ width: 120 }}
                value={filterStatus}
                onChange={setFilterStatus}
              >
                <Option value="all">全部状态</Option>
                <Option value="active">上架中</Option>
                <Option value="inactive">已下架</Option>
              </Select>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button icon={<ExportOutlined />}>导出</Button>
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                customRequest={handleImport}
              >
                <Button icon={<UploadOutlined />} loading={importing}>
                  批量导入
                </Button>
              </Upload>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                下载模板
              </Button>
            </Space>
            {selectedRowKeys.length > 0 && (
              <div
                style={{
                  marginBottom: 16,
                  padding: '8px 16px',
                  background: '#e6f7ff',
                  borderRadius: 4,
                }}
              >
                <Space>
                  <span>已选择 {selectedRowKeys.length} 项</span>
                  <Button size="small" onClick={() => handleBatchToggle(true)}>
                    批量上架
                  </Button>
                  <Button size="small" onClick={() => handleBatchToggle(false)}>
                    批量下架
                  </Button>
                  <Button size="small" danger onClick={handleBatchDelete}>
                    批量删除
                  </Button>
                  <Button size="small" onClick={() => setSelectedRowKeys([])}>
                    取消选择
                  </Button>
                </Space>
              </div>
            )}
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                添加商品
              </Button>
            </div>
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={products}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1100 }}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: total => `共 ${total} 条`,
                onChange: (page, pageSize) =>
                  setPagination(prev => ({ ...prev, current: page, pageSize })),
              }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="分类管理" key="category">
            <div
              style={{
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Space>
                <Select
                  style={{ width: 150 }}
                  placeholder="筛选层级"
                  allowClear
                  value={categoryFilter}
                  onChange={val => setCategoryFilter(val)}
                  onClear={() => setCategoryFilter(undefined)}
                >
                  <Option value={1}>一级分类</Option>
                  <Option value={2}>二级分类</Option>
                  <Option value={3}>三级分类</Option>
                </Select>
                <Space>
                  <Button
                    type={categoryViewMode === 'table' ? 'primary' : 'default'}
                    onClick={() => setCategoryViewMode('table')}
                  >
                    表格视图
                  </Button>
                  <Button
                    type={categoryViewMode === 'tree' ? 'primary' : 'default'}
                    onClick={() => setCategoryViewMode('tree')}
                  >
                    树形视图
                  </Button>
                </Space>
              </Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCategory}>
                添加分类
              </Button>
            </div>
            {categoryViewMode === 'table' ? (
              <Table
                columns={[
                  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
                  { title: '分类名称', dataIndex: 'name', key: 'name', width: 180 },
                  {
                    title: '层级',
                    dataIndex: 'level',
                    key: 'level',
                    width: 100,
                    render: (v: number) => {
                      const m: Record<number, { color: string; text: string }> = {
                        1: { color: 'blue', text: '一级分类' },
                        2: { color: 'green', text: '二级分类' },
                        3: { color: 'orange', text: '三级分类' },
                      }
                      return <Tag color={m[v]?.color}>{m[v]?.text || v}</Tag>
                    },
                  },
                  {
                    title: '父级分类',
                    dataIndex: 'parentId',
                    key: 'parentId',
                    width: 130,
                    render: (val: number | null) => {
                      if (!val) return <Tag color="default">顶级分类</Tag>
                      const p = flattenedCategories.find(c => c.id === val)
                      return p?.name || '-'
                    },
                  },
                  { title: 'sort', dataIndex: 'sort', key: 'sort', width: 60 },
                  {
                    title: '操作',
                    key: 'action',
                    width: 220,
                    fixed: 'right' as const,
                    render: (_: any, record: Category, index: number) => (
                      <Space size="small">
                        <Button
                          type="link"
                          size="small"
                          icon={<ArrowUpOutlined />}
                          onClick={() => handleMoveCategory(index, 'up')}
                        />
                        <Button
                          type="link"
                          size="small"
                          icon={<ArrowDownOutlined />}
                          onClick={() => handleMoveCategory(index, 'down')}
                        />
                        <Button
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEditCategory(record)}
                        >
                          编辑
                        </Button>
                        <Popconfirm
                          title="确定删除吗？"
                          onConfirm={() => handleDeleteCategory(record.id, record.name)}
                        >
                          <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                          >
                            删除
                          </Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
                dataSource={flattenedCategories.filter(
                  c => !categoryFilter || c.level === categoryFilter
                )}
                rowKey="id"
                pagination={{ showSizeChanger: true, showTotal: total => `共 ${total} 条` }}
                scroll={{ x: 1000 }}
              />
            ) : (
              <Card size="small">
                <Tree
                  treeData={buildCategoryTree()}
                  defaultExpandAll
                  showLine={{ showLeafIcon: false }}
                />
              </Card>
            )}
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* 商品编辑弹窗 */}
      <Modal
        title={editingProduct ? '编辑商品' : '添加商品'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false)
          setEditingProduct(null)
        }}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ skus: [], images: [], introImages: [], isActive: true }}
        >
          <Tabs
            defaultActiveKey="basic"
            items={[
              {
                key: 'basic',
                label: '基本信息与规格',
                children: (
                  <>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="name"
                          label="商品名称"
                          rules={[
                            { required: true, message: '请输入商品名称' },
                            { max: 100, message: '商品名称最多100个字符' },
                          ]}
                        >
                          <Input placeholder="请输入商品名称" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="categoryId"
                          label="商品分类"
                          rules={[{ required: true, message: '请选择商品分类' }]}
                        >
                          <Select
                            placeholder="请选择分类"
                            showSearch
                            optionFilterProp="children"
                          >
                            {flattenedCategories.map(c => (
                              <Option key={c.id} value={c.id}>
                                {'-'.repeat(c.level - 1)} {c.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      name="description"
                      label="商品描述"
                      rules={[{ max: 500, message: '描述最多500个字符' }]}
                    >
                      <TextArea rows={2} placeholder="请输入商品描述" />
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="商品主图"
                          rules={[{ required: true, message: '请上传商品主图' }]}
                        >
                          {previewImage ? (
                            <div
                              style={{ position: 'relative', display: 'inline-block' }}
                            >
                              <img
                                src={getImageUrl(previewImage)}
                                alt="主图"
                                style={{
                                  width: 102,
                                  height: 102,
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                  border: '1px solid #ddd',
                                }}
                              />
                              <Button
                                size="small"
                                danger
                                style={{
                                  position: 'absolute',
                                  top: -10,
                                  right: -10,
                                  borderRadius: '50%',
                                }}
                                onClick={() => {
                                  setPreviewImage('')
                                  form.setFieldsValue({ image: '' })
                                }}
                              >
                                ×
                              </Button>
                            </div>
                          ) : (
                            <Upload
                              customRequest={({ file }) =>
                                handleImageUpload(file as File)
                              }
                              listType="picture-card"
                              maxCount={1}
                              accept="image/*"
                              showUploadList={false}
                            >
                              <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>上传图片</div>
                              </div>
                            </Upload>
                          )}
                        </Form.Item>
                        <Form.Item name="image" hidden>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="商品轮播图">
                          <div
                            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                          >
                            {previewImages.map((img, index) => (
                              <div
                                key={index}
                                style={{
                                  position: 'relative',
                                  display: 'inline-block',
                                }}
                              >
                                <img
                                  src={getImageUrl(img)}
                                  alt="轮播图"
                                  style={{
                                    width: 102,
                                    height: 102,
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    border: '1px solid #ddd',
                                  }}
                                />
                                <Button
                                  size="small"
                                  danger
                                  style={{
                                    position: 'absolute',
                                    top: -10,
                                    right: -10,
                                    borderRadius: '50%',
                                  }}
                                  onClick={() => {
                                    const imgs = [...previewImages]
                                    imgs.splice(index, 1)
                                    setPreviewImages(imgs)
                                    form.setFieldsValue({ images: imgs })
                                  }}
                                >
                                  ×
                                </Button>
                              </div>
                            ))}
                            <Upload
                              customRequest={({ file }) =>
                                handleImagesUpload(file as File)
                              }
                              listType="picture-card"
                              accept="image/*"
                              showUploadList={false}
                            >
                              <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>上传图片</div>
                              </div>
                            </Upload>
                          </div>
                        </Form.Item>
                        <Form.Item name="images" hidden>
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="isActive" label="状态" valuePropName="checked">
                      <Switch checkedChildren="上架" unCheckedChildren="下架" />
                    </Form.Item>
                    <div
                      style={{
                        margin: '24px 0 16px',
                        borderTop: '1px solid #f0f0f0',
                      }}
                    >
                      <span
                        style={{
                          position: 'relative',
                          top: -12,
                          background: '#fff',
                          paddingRight: 12,
                          fontWeight: 'bold',
                          color: '#333',
                        }}
                      >
                        商品规格
                      </span>
                    </div>
                    <Form.List name="skus">
                      {(fields, { add, remove }) => {
                        if (!Array.isArray(fields))
                          return <div>暂无规格数据</div>
                        return (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Card
                                key={key}
                                size="small"
                                style={{ marginBottom: 16 }}
                                title={
                                  <span style={{ fontSize: 14 }}>
                                    规格 {name + 1}：
                                    {form.getFieldValue(['skus', name, 'spec']) ||
                                      '未命名'}
                                  </span>
                                }
                                extra={
                                  <Button
                                    danger
                                    size="small"
                                    onClick={() => remove(name)}
                                  >
                                    删除
                                  </Button>
                                }
                              >
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'spec']}
                                      label="规格名称"
                                      rules={[
                                        { required: true, message: '请输入规格名称' },
                                      ]}
                                    >
                                      <Input placeholder="如：25kg/袋" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'stock']}
                                      label="库存"
                                      rules={[
                                        { required: true, message: '请输入库存' },
                                      ]}
                                    >
                                      <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        placeholder="库存"
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'stockWarning']}
                                      label="库存预警"
                                    >
                                      <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        placeholder="预警值"
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: '#999',
                                    marginTop: 8,
                                  }}
                                >
                                  注：价格、促销方案、生产日期请在「价格日期」模块管理
                                </div>
                              </Card>
                            ))}
                            <Button
                              type="dashed"
                              onClick={() =>
                                add({ spec: '', stock: 0, stockWarning: 10 })
                              }
                              block
                              icon={<PlusOutlined />}
                            >
                              添加规格
                            </Button>
                          </>
                        )
                      }}
                    </Form.List>
                  </>
                ),
              },
              {
                key: 'params',
                label: '参数与介绍',
                children: (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <span
                        style={{ fontWeight: 'bold', color: '#333', fontSize: 14 }}
                      >
                        商品参数
                      </span>
                    </div>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="storageTemp" label="储存温度">
                          <Input placeholder="如：常温、阴凉干燥处" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="origin" label="产地">
                          <Input placeholder="如：中国山东" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="shelfLife" label="保质期">
                          <Input placeholder="如：12个月" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <div
                      style={{
                        margin: '24px 0 16px',
                        borderTop: '1px solid #f0f0f0',
                      }}
                    >
                      <span
                        style={{
                          position: 'relative',
                          top: -12,
                          background: '#fff',
                          paddingRight: 12,
                          fontWeight: 'bold',
                          color: '#333',
                        }}
                      >
                        商品介绍
                      </span>
                    </div>
                    <Form.Item name="introText" label="介绍文本">
                      <TextArea rows={5} placeholder="请输入商品详细介绍" />
                    </Form.Item>
                    <Form.Item label="介绍图片">
                      <div
                        style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                      >
                        {previewIntroImages.map((img, index) => (
                          <div
                            key={index}
                            style={{
                              position: 'relative',
                              display: 'inline-block',
                            }}
                          >
                            <img
                              src={getImageUrl(img)}
                              alt="介绍图"
                              style={{
                                width: 102,
                                height: 102,
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid #ddd',
                              }}
                            />
                            <Button
                              size="small"
                              danger
                              style={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                borderRadius: '50%',
                              }}
                              onClick={() => {
                                const imgs = [...previewIntroImages]
                                imgs.splice(index, 1)
                                setPreviewIntroImages(imgs)
                                form.setFieldsValue({ introImages: imgs })
                              }}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                        <Upload
                          customRequest={({ file }) =>
                            handleIntroImagesUpload(file as File)
                          }
                          listType="picture-card"
                          accept="image/*"
                          showUploadList={false}
                        >
                          <div>
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>上传图片</div>
                          </div>
                        </Upload>
                      </div>
                    </Form.Item>
                    <Form.Item name="introImages" hidden>
                      <Input />
                    </Form.Item>
                  </>
                ),
              },
            ]}
          />
        </Form>
      </Modal>

      {/* 分类编辑弹窗 */}
      <Modal
        title={editingCategory ? '编辑分类' : '添加分类'}
        open={categoryModalVisible}
        onOk={handleCategorySubmit}
        onCancel={() => setCategoryModalVisible(false)}
        width={500}
        destroyOnClose
      >
        <Form form={categoryForm} layout="vertical" initialValues={{ level: 1 }}>
          <Form.Item
            name="name"
            label="分类名称"
            rules={[
              { required: true, message: '请输入分类名称' },
              { max: 20, message: '分类名称不能超过20个字符' },
            ]}
          >
            <Input placeholder="请输入分类名称" />
          </Form.Item>
          <Form.Item
            name="level"
            label="分类层级"
            rules={[{ required: true, message: '请选择层级' }]}
          >
            <Select
              placeholder="请选择层级"
              onChange={handleCategoryLevelChange}
              disabled={!!editingCategory}
            >
              <Option value={1}>一级分类</Option>
              <Option value={2}>二级分类</Option>
              <Option value={3}>三级分类</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="parentId"
            label="父级分类"
            tooltip="选择所属的上级分类"
          >
            <Select
              placeholder="请选择父级分类"
              allowClear
              disabled={!categoryForm.getFieldValue('level')}
            >
              <Option value={undefined}>无（顶级分类）</Option>
              {flattenedCategories
                .filter(
                  c =>
                    categoryForm.getFieldValue('level') === c.level + 1 &&
                    (!editingCategory || c.id !== editingCategory.id)
                )
                .map(c => (
                  <Option key={c.id} value={c.id}>
                    {c.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          {categoryForm.getFieldValue('parentId') && (
            <div
              style={{
                padding: '8px 12px',
                background: '#f5f5f5',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: 13,
                color: '#666',
              }}
            >
              <span>分类路径：</span>
              {(() => {
                const parentId = categoryForm.getFieldValue('parentId')
                const parent = flattenedCategories.find(c => c.id === parentId)
                const grandParent = parent?.parentId
                  ? flattenedCategories.find(c => c.id === parent.parentId)
                  : null
                return (
                  <span>
                    {grandParent && (
                      <span>
                        {grandParent.name} {' > '}
                      </span>
                    )}
                    {parent && (
                      <span>
                        {parent.name} {' > '}
                      </span>
                    )}
                    <span style={{ color: '#006835', fontWeight: 500 }}>
                      {categoryForm.getFieldValue('name') || '新分类'}
                    </span>
                  </span>
                )
              })()}
            </div>
          )}
        </Form>
      </Modal>
    </div>
  )
}

export default Products
